import { Stepper, TransitionGroup } from "components";
import { _bemHelper } from "components/orders/Fields/FieldHelpers";
import type { BaseDeckFormType } from "components/orders/Forms/Partials/Features/Feature/FeatureSchema";
import SaveButton from "components/orders/SaveButton/SaveButton";
import RevertQuoteButton from "components/RevertQuoteButton/RevertQuoteButton";
import TransparentBar from "components/TransparentBar";
import { OrderStatus, OrderType, type Resource } from "models";
import {
  type DivingBoardFastenType,
  type Feature,
  FeatureType,
  labelFeatures,
  type MoldedStepFace,
} from "models/Feature";
import type { ModelInstance } from "models/Model";
import {
  type FeatureMeasurements,
  type Project,
  ProjectModel,
  ProjectStatus,
} from "models/Project";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Cross from "res/img/cross.svg?react";
import {
  Card,
  Confirm,
  Dropdown,
  Form,
  Header,
  Modal,
  Ref,
  Segment,
  Tab,
} from "semantic-ui-react";
import { useAuth, useUI } from "services";
import { ORDER_MSG, USER_MSG, getTranslatedUserMsg } from "strings";
import { catchAndThrowErrors, checkFeatureAuthorization, Features } from "utils";
import { OrdersService } from ".";
import {
  OrderModal,
  type OrderModalTitle,
} from "../../components/orders/OrderModal/OrderModal";
import { FIRST_STEP } from "../../components/orders/Stepper/Stepper";
import {
  type accessoriesOrderState,
  type anchorsOrderState,
  type featureOrderState,
  OrderContext,
  type OrderState,
  type projectOrderState,
  type shippingOrderState,
} from "../../context/OrderContext";
import {
  type OrderBody,
  type OrderQuote,
  type ServerOrder,
  type ServerProduct,
  OrderQuoteStatus,
  PriceOptionOptions,
} from "../../models/Order";
import { ProjectService } from "../projects";
import "./Orders.scss";
import {
  checkFeatureOrderType,
  isLinerFeature,
  isVinylFeature,
} from "components/orders/Forms/Partials/Features/Feature/FeatureConstants";
import { useFeatureFlags } from "services";
import "../projects/ProjectMeasurement.scss";
import { Role } from "models/Role";
import { useT } from "i18n";
import deleteIcon from "../../img/delete_icon.svg";
import archiveIcon from "../../img/archive_icon.svg";

const INITIAL_STEP = 0;
type template = (vars: { [key: string]: any }) => string;
const EMAIL = {
  to: "jasonkeith@lathampool.com, billgregware@lathampool.com",
  subject: (({ dealer, project }) =>
    encodeURIComponent(`Latham Measure Order - ${dealer} - ${project}`)) as template,
  body: (({ url, msg }) =>
    encodeURIComponent(`-------------[ PROJECT DATA ]-------------

${url}
-------------[ DEALER MESSAGE ]-------------
${msg}
------------------[ END ]--------------------
`)) as template,
};

type OrdersProps = {
  project: Project;
  isProjectValid: boolean;
  updateProject: React.Dispatch<React.SetStateAction<ModelInstance<Project>>>;
  fetchProject: () => Promise<void>;
  generateDxf: () => Promise<void>;
  isDistributorDealer: boolean;
};

export type LabeledResources = {
  id?: string;
  label: string;
  type: FeatureType;
  featureName: string;
  notes?: string;
  images: Resource[];
  length?: number;
  isRemovable?: boolean;
  isCovered?: boolean;
  measurement?: FeatureMeasurements;
  stepMaterialOption?: string;
  stepMaterial?: string;
  stepStripe?: string;
  stripeWidth?: number;
  moldedStepFace?: MoldedStepFace;
  differentTreads?: string;
  riserMaterial?: string;
  stepFastener?: string;
  stepComment?: string;
  divingBoardFastenType?: DivingBoardFastenType;
};

export const getDateAndTime = (label: string, dateString?: string): string => {
  if (!dateString) {
    return "";
  }

  const dateObject = new Date(dateString);
  const date = dateObject.toLocaleDateString();
  const time = dateObject.toLocaleTimeString();

  return `${label} on ${date} at ${time}`;
};

type Error = {
  statusCode: number;
  message: string;
  error: string;
};

const OrderCard = ({
  hasArchiveAccess,
  hasDeleteAccess,
  isDistributorDealer,
  order,
  openModal,
  confirmArchiveOrder,
  confirmDeleteOrder,
  getQuoteDateText,
  getLatestQuote,
}: {
  hasArchiveAccess: boolean;
  hasDeleteAccess: boolean;
  isDistributorDealer: boolean;
  order: ServerOrder;
  openModal: (order: ServerOrder) => void;
  confirmArchiveOrder: (e: React.MouseEvent<HTMLElement>, order: ServerOrder) => void;
  confirmDeleteOrder: (e: React.MouseEvent<HTMLElement>, order: ServerOrder) => void;
  getQuoteDateText: (order: ServerOrder) => string;
  getLatestQuote: (order: ServerOrder) => OrderQuote | undefined;
}) => {
  const t = useT();
  // If no price option is set, default to list price per Stuart
  const [urlPriceOption, setUrlPriceOption] = useState(() => {
    const option = PriceOptionOptions.find((opt) => opt.value === order.priceOption);
    return option?.value ?? PriceOptionOptions[0].value;
  });

  const getLatestQuoteUrl = (order: ServerOrder): string => {
    const latestOrder = getLatestQuote(order);

    if (latestOrder) {
      const quoteUrl = latestOrder?.lathamLinkResponse?.quoteOrderUrl;

      if (quoteUrl) {
        if (!isDistributorDealer) {
          return quoteUrl;
        }

        const price = (() => {
          switch (urlPriceOption) {
            case t("component.orders.netPrice", "Net Price"):
              return "346";
            case t("component.orders.listPrice", "List Price"):
              return "787";
            case "None":
              return "999";
            default:
              return "346";
          }
        })();

        if (quoteUrl.includes("Price=")) {
          return quoteUrl.replace(/Price=\d+/, `Price=${price}`);
        } else {
          return quoteUrl + `&Price=${price}`;
        }
      }
    }

    return "";
  };

  return (
    <>
      <Card
        className={"ui card order-card"}
        onClick={() => {
          openModal(order);
        }}
        style={{
          background: order.archived ? "#e0e1e2" : "#fff",
        }}
      >
        <Card.Header style={{ margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className={"order-card-header-title"}>
              {order?.type === OrderType.Liner
                ? t("component.orders.liner", "Liner")
                : t("component.orders.cover", "Cover")}
            </div>
            <div>
              {hasArchiveAccess && (
                <div className={"archive-icon"}>
                  <span
                    className={`tooltiptext archive-tooltiptext ${
                      order?.archived ? "unarchive" : "archive"
                    }`}
                  >
                    {t("component.orders.clickTo", "Click to")}{" "}
                    {order?.archived
                      ? t("orderMsg.unarchive", ORDER_MSG.UNARCHIVE)
                      : t("orderMsg.archive", ORDER_MSG.ARCHIVE)}
                  </span>
                  <img
                    onClick={(e) => {
                      confirmArchiveOrder(e, order);
                    }}
                    src={archiveIcon}
                    alt={t("component.orders.archive", "archive")}
                    style={{ width: "2.56rem", height: "2.375rem" }}
                    className={"archive-icon-btn"}
                  />
                </div>
              )}
              {hasDeleteAccess &&
                (order?.status === OrderStatus.Saved ||
                  order?.status === OrderStatus.QuotedOptions ||
                  order?.status === OrderStatus.QuoteInReview ||
                  order?.status === OrderStatus.ReadyForOrder) && (
                  <div className={"delete-icon"}>
                    <span className={"tooltiptext delete-tooltiptext"}>
                      {t("component.orders.clickToDelete", "Click to Delete")}
                    </span>
                    <img
                      onClick={(e) => {
                        confirmDeleteOrder(e, order);
                      }}
                      src={deleteIcon}
                      alt={t("component.common.delete", "Delete")}
                      style={{
                        width: "2.3125rem",
                        height: "2.375rem",
                        gap: "0.625rem",
                      }}
                      className={"delete-icon-btn"}
                    />
                  </div>
                )}
            </div>
          </div>
        </Card.Header>
        <TransparentBar />
        {!!order.quoteId && (
          <Card.Meta style={{ margin: 0 }}>
            {t("component.orders.submittedBy", "Submitted By")}
            <Card.Description>
              <div className={"order-card-text"}>
                {order.requestedByUserName ?? t("component.orders.unknown", "Unknown")}
              </div>
            </Card.Description>
          </Card.Meta>
        )}
        <TransparentBar />
        <Card.Meta style={{ margin: 0 }}>
          {t("component.common.status", "Status")}
          <Card.Description>
            {order?.lathamSpec?.isScanRejected ? (
              <div className={"order-card-text"} style={{ color: "var(--color-red)" }}>
                {getDateAndTime(
                  t("component.orders.rejected", "Rejected"),
                  order.lathamSpec.scanRejectedAt,
                )}
              </div>
            ) : (
              <div className={"order-card-text"}>{getQuoteDateText(order)}</div>
            )}
          </Card.Description>
          {order.quotes &&
            order.quotes?.filter((quote) => quote.status === OrderQuoteStatus.Valid)
              .length > 0 &&
            (order.status === OrderStatus.Ordered ||
              order.status === OrderStatus.ReadyForOrder) && (
              <Card.Description>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    className={"order-card-text"}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <a
                      href={order.quoteId ? getLatestQuoteUrl(order) : ""}
                      rel={"noreferrer"}
                      target={"_blank"}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {order.status === OrderStatus.Ordered
                        ? t("component.orders.viewOrderDetails", "View Order Details")
                        : t("component.orders.viewQuoteDetails", "View Quote Details")}
                    </a>
                  </div>
                  {isDistributorDealer && (
                    <div
                      style={{
                        minWidth: "112px",
                      }}
                    >
                      <Dropdown
                        fluid
                        selection
                        name={"priceOption"}
                        compact
                        placeholder={t(
                          "component.orders.selectPriceOption",
                          "Select Price Option",
                        )}
                        options={PriceOptionOptions.map((value, i) => ({
                          key: i,
                          text: value.label,
                          value: value.value,
                        }))}
                        value={urlPriceOption}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onChange={(e, data) => {
                          setUrlPriceOption(data.value as string);
                        }}
                      />
                    </div>
                  )}
                </div>
              </Card.Description>
            )}
          {order?.status === OrderStatus.ReadyForOrder && (
            <button className={"ui primary button create-order-button"}>
              {t("component.orders.createOrder", "Create Order")}
            </button>
          )}
        </Card.Meta>
      </Card>
    </>
  );
};

export default function Orders({
  project,
  isProjectValid,
  updateProject,
  fetchProject,
  generateDxf,
  isDistributorDealer,
}: OrdersProps): React.ReactElement {
  const t = useT();
  const emailMsg = useRef({} as HTMLTextAreaElement);
  const { state, dispatch } = React.useContext(OrderContext);
  const flags = useFeatureFlags();

  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [orders, setOrders] = useState([] as ServerOrder[]);
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
  const [isLastStep, setIsLastStep] = useState(false);
  const [submitUrl, setSubmitUrl] = useState(null as string | null);
  const [confirmButtonText, setConfirmButtonText] = useState<string>("");
  const [modalTitle, setModalTitle] = useState<OrderModalTitle>("New Quote");
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [waitingForLathamResponse, setWaitingForLathamResponse] = useState(false);
  const { showError, showSuccess } = useUI();
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [isArchiveConfirmModalOpen, setIsArchiveConfirmModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<ServerOrder | undefined>();
  const [confirmHeaderText, setConfirmHeaderText] = useState("");
  const [confirmBodyText, setConfirmBodyText] = useState("");
  const { user } = useAuth();
  const hasAccess = user?.role !== Role.DESIGN && user?.role !== Role.SALES;
  const hasReviewAccess = user?.role !== Role.ADMIN;
  const hasDeleteAccess =
    user?.role !== Role.CUST_SERV &&
    user?.role !== Role.DESIGN &&
    user?.role !== Role.USER;
  const hasRevertAccess =
    user?.role === Role.CUST_SERV ||
    user?.role === Role.LATHAM_ADMIN ||
    user?.role === Role.BAI_ADMIN;
  const hasUnRejectAccess =
    user?.role === Role.CUST_SERV ||
    user?.role === Role.LATHAM_ADMIN ||
    user?.role === Role.BAI_ADMIN;
  const hasArchiveAccess = user?.role != Role.DESIGN;
  const LAST_STEP = state.steps.length - 1;

  const checkScanRejection = (): boolean => {
    // If the current order has been rejected, then any further actions are prohibited
    if (state.order?.lathamSpec?.isScanRejected === true) {
      showError(USER_MSG.ERROR_PROJECT_SCAN_REJECTED);
      return true;
    }
    return false;
  };

  useEffect(() => {
    setModalTitle(getTitle());
  }, [currentStep]);

  const fetchOrders = async () => {
    try {
      const freshOrders = await ProjectService.getOrders(project.id);
      setOrders(
        freshOrders.sort((a, b) => {
          return a.createdAt > b.createdAt ? 1 : -1;
        }),
      );

      //@todo This needs to be handled in react state instead of mutating an object
      project.orders = freshOrders;
    } catch (e) {
      console.log(e);
    }
  };

  const fetchProjectAndOrders = async () => {
    await Promise.all([fetchProject(), fetchOrders()]);
  };

  useEffect(() => {
    fetchOrders();
  }, [JSON.stringify(state.order)]);

  // TODO move this out of this component?
  const fetchFeatures = async () => {
    try {
      const allFeatures = await ProjectService.getFeatures(project.id);
      const { features, decks: decksData } = labelFeatures(allFeatures, true);
      const featuresData = features.filter((feat) =>
        checkFeatureOrderType(feat.type, state.project?.type ?? ""),
      );

      dispatch({
        type: "updateState",
        payload: {
          featuresData,
          decksData,
        } as Partial<OrderState>,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const openModal = (order?: ServerOrder): void => {
    if (project?.poolDetails?.poolShape?.baseName) {
      if (order && order?.features && order?.accessories) {
        dispatch({
          type: "updateState",
          payload: {
            order,
            features: order.features as featureOrderState,
            anchors: order.anchors as anchorsOrderState,
            accessories: order.accessories as accessoriesOrderState,
            shipping: order.shipping as shippingOrderState,
            project: {
              ...state.project,
              isDistributorDealer: isDistributorDealer && flags.dealerDistributor,
              customerNumber: project.customerNumber,
            } as projectOrderState,
            //@ts-expect-error @todo fix - product type may not match exactly
            product: order.product,
          },
        });
      } else {
        dispatch({
          type: "updateState",
          payload: {
            ...state,
            features: { ...state.features, isViewed: false },
            accessories: { ...state.accessories, isViewed: false },
            project: {
              ...state.project,
              isDistributorDealer: isDistributorDealer && flags.dealerDistributor,
              customerNumber: project.customerNumber,
            },
          },
        });
      }
      setCurrentStep(FIRST_STEP);
      setIsOrderModalOpen(true);
    } else {
      showError(getTranslatedUserMsg(t, "ERROR_NEW_QUOTE"));
    }
  };

  const goToStep = (step: number) => {
    // Save project when navigating away from project step only
    if (currentStep === FIRST_STEP) {
      if (checkFeatureAuthorization(Features.SaveProject, user?.role)) {
        saveProject();
      }
    }
    setCurrentStep(step);
  };

  const openConfirmationModal = async () => {
    submitToLatham();
  };

  const openEmailClient = useCallback(() => {
    const { to, subject, body } = EMAIL;
    const mailto = `mailto:${to}?subject=${subject({
      project: project.name,
      dealer: project.dealerId,
    })}&body=${body({ url: submitUrl, msg: emailMsg.current?.value })}`;
    window.open(mailto, "_blank");
    setIsConfirmModalOpen(false);
    dispatch({ type: "resetOrder" });
    setSubmitUrl(null);
  }, [project.name, project.dealerId, emailMsg, submitUrl]);

  const cancelOrder = useCallback(() => {
    setIsOrderModalOpen(false);
    dispatch({ type: "resetOrder" });
  }, [project.dealerId, project.id]);

  const createOrder = async (submitOrder?: boolean) => {
    const proj = state.project;
    delete proj.poNumber;

    const newOrder: OrderBody = {
      product: state.product as ServerProduct,
      project: state.project,
      features: state.features,
      anchors: state.anchors,
      accessories: state.accessories,
      shipping: state.shipping,
      lathamSpec: state.spec,
      projectId: project.id,
      dealerId: project.dealerId,
      poNumber: state.project.poNumber,
      type: state.project.type,
      status: OrderStatus.Saved,
      requestedByUserId: user?.id,
      requestedByEmail: user?.email,
      priceOption: state.project.priceOption,
    };
    await OrdersService.add(newOrder)
      .then(async (order) => {
        dispatch({
          type: "updateState",
          payload: {
            order: order as ServerOrder,
          },
        });
        fetchOrders();
        setIsOrderModalOpen(false);

        // saved -> quote in review -> ready for order -> submitted
        const needsReview = quoteNeedsReview();
        if (submitOrder) {
          if (!isProjectValid) {
            showError(USER_MSG.ERROR_INCOMPLETE_PROJECT);
          } else if (order?.id && !waitingForLathamResponse) {
            let request: Function;
            let successMessage: { title: string; msg: string };
            if (order.status === OrderStatus.ReadyForOrder) {
              request = ProjectService.submitQuotedOrder;
              successMessage = getTranslatedUserMsg(t, "SUCCESS_ORDER_SUBMIT");
            } else if (order.status === OrderStatus.Saved && needsReview) {
              request = ProjectService.submitQuoteForReview;
              successMessage = getTranslatedUserMsg(t, "SUCCESS_QUOTE_SUBMIT");
            } else {
              request = ProjectService.submitOrderForQuote;
              successMessage = getTranslatedUserMsg(t, "SUCCESS_QUOTE_SUBMIT");
            }

            setWaitingForLathamResponse(true);
            await request(project.id, order.id)
              .then(async () => {
                showSuccess(successMessage);
                await fetchProjectAndOrders();
              })
              .catch((error: Error) => {
                if (error.message.includes("LathamLink")) {
                  console.log("LathamLink error");
                  catchAndThrowErrors(
                    error,
                    showError,
                    getTranslatedUserMsg(t, "ERROR_ORDER_DATA"),
                  );
                } else {
                  catchAndThrowErrors(
                    error,
                    showError,
                    getTranslatedUserMsg(t, "ERROR_MEASUREMENT_DATA"),
                  );
                }
              })
              .finally(() => {
                setWaitingForLathamResponse(false);
                setIsConfirmModalOpen(false);
                dispatch({ type: "resetOrder" });
              });
          }
        }
      })
      .catch((error) => {
        catchAndThrowErrors(
          error,
          showError,
          getTranslatedUserMsg(t, "ERROR_CREATING_ORDER"),
        );
      });
  };

  const saveProject = async () => {
    if (checkScanRejection()) {
      return;
    }

    try {
      // features are saved in the project object so we get the feature from there, then update it with the
      // updated feature information from the order
      const newFeatures: Feature[] = [];
      let triggerDxfGeneration = false;

      if (project?.features) {
        project?.features.forEach((el: Feature, i: number) => {
          const stateFeat = state.features.features
            ? state.features.features.filter((f: BaseDeckFormType) => f.id === el.id)[0]
            : undefined;

          if (project?.features) {
            // If DXF generation is required, add a condition below to trigger it
            const notesUpdated = stateFeat?.notes?.trim() !== el?.notes?.trim();
            const coverExtensionUpdated =
              stateFeat?.featureType === "AutoCover" && !!stateFeat?.coverExtension;
            if (currentStep !== FIRST_STEP && (notesUpdated || coverExtensionUpdated)) {
              triggerDxfGeneration = true;
            }

            const feat = {
              ...project?.features[i],
              notes: stateFeat?.notes ?? el?.notes,
              isRemovable: stateFeat?.isRemovable ?? false,
              isCovered: stateFeat?.isCovered ?? false,
            };
            // @ts-expect-error - isOverWater property may not exist on all feature types
            delete feat.isOverWater;
            newFeatures.push(feat);
          }
        });
      }

      // Update the project's address
      const newProject = {
        ...project,
        id: project.id,
        address: {
          address1: state.project.address1,
          address2: state.project.address2,
          city: state.project.city,
          state: state.project.shippingState,
          postalcode: state.project.postalCode,
        },
        customer: {
          name: state.project.primaryContactName,
          email: project.customer?.email,
          phone: project.customer?.phone,
        },
        features: newFeatures,
      };

      const hasProjectChanged = JSON.stringify(project) !== JSON.stringify(newProject);

      if (hasProjectChanged || triggerDxfGeneration) {
        // trim data to transfer
        const data = { ...newProject };
        delete data.resources;
        delete data.scans;
        delete data.orders;
        delete data.dealerName;

        // update the project and refresh the form data
        await ProjectService.set(data, "PATCH", false).catch((error) => {
          catchAndThrowErrors(
            error,
            showError,
            getTranslatedUserMsg(t, "ERROR_SAVING_PROJECT"),
          );
        });
        updateProject(ProjectModel({ ...newProject }));
        if (triggerDxfGeneration) {
          generateDxf();
        }
      }
    } catch (err) {
      const error = err as Error;
      catchAndThrowErrors(error, showError, USER_MSG.ERROR_SAVING_PROJECT);
    }
  };

  const restoreScan = async () => {
    try {
      if (!hasUnRejectAccess) {
        showError(getTranslatedUserMsg(t, "ERROR_UNAUTH_ROUTE"));
        return;
      }

      if (!state.order?.id) {
        showError(USER_MSG.ERROR_ORDER_ID_MISSING);
        return;
      }

      // Remove rejection data from the lathamSpec JSON column
      const updatedLathamSpec = {
        ...(state.order.lathamSpec || {}),
        isScanRejected: false,
        scanRejectedAt: undefined,
      };

      const updatedOrder: ServerOrder = {
        ...state.order,
        lathamSpec: updatedLathamSpec as any,
      };

      // Remove fields that shouldn't be in the update payload
      delete updatedOrder.requestedBy;
      delete updatedOrder.project;

      await OrdersService.set(updatedOrder, "PATCH", false);
      showSuccess(getTranslatedUserMsg(t, "SUCCESS_SCAN_UNREJECT"));
      await fetchProjectAndOrders();
      setIsOrderModalOpen(false);
      dispatch({ type: "resetOrder" });
    } catch (error) {
      catchAndThrowErrors(
        error,
        showError,
        getTranslatedUserMsg(t, "ERROR_MEASUREMENT_DATA"),
      );
    }
  };

  const rejectScan = async (customMessage?: string) => {
    if (checkScanRejection()) {
      return;
    }

    try {
      if (!state.order?.id) {
        showError(USER_MSG.ERROR_ORDER_ID_MISSING);
        return;
      }

      // Store rejection data in the lathamSpec JSON column
      const updatedLathamSpec = {
        ...(state.order.lathamSpec || {}),
        isScanRejected: true,
        scanRejectedAt: new Date().toISOString(),
      };

      const updatedOrder: ServerOrder & {
        isScanRejected?: boolean;
        scanRejectedReason?: string;
      } = {
        ...state.order,
        lathamSpec: updatedLathamSpec as any,
        // Include rejection data at top level for backend email trigger
        isScanRejected: true,
        scanRejectedReason: customMessage,
      };

      // Remove fields that shouldn't be in the update payload
      delete updatedOrder.requestedBy;
      delete updatedOrder.project;

      // Backend will automatically send rejection email if isScanRejected and scanRejectedReason are present
      await OrdersService.set(updatedOrder, "PATCH", false);
      showSuccess(USER_MSG.SUCCESS_SCAN_REJECT);
      await fetchProjectAndOrders();
      setIsOrderModalOpen(false);
      dispatch({ type: "resetOrder" });
    } catch (error) {
      catchAndThrowErrors(
        error,
        showError,
        getTranslatedUserMsg(t, "ERROR_MEASUREMENT_DATA"),
      );
    }
  };

  const revertQuote = async () => {
    if (checkScanRejection()) {
      return;
    }

    try {
      const newOrder = {
        ...state.order,
        status: OrderStatus.QuoteInReview,
        isRevert: true,
      } as ServerOrder & { isRevert: boolean };

      if (state.order?.id) {
        await OrdersService.set(newOrder, "PATCH", false).then(async () => {
          const { resources, scans, orders, dealerName, ...rest } = project;
          const projectData = { ...rest, status: ProjectStatus.QUOTE_IN_REVIEW };
          await ProjectService.set(projectData, "PATCH", false).catch((error) => {
            catchAndThrowErrors(
              error,
              showError,
              getTranslatedUserMsg(t, "ERROR_SAVING_PROJECT"),
            );
          });
          showSuccess(getTranslatedUserMsg(t, "SUCCESS_QUOTE_REVERT"));
          await fetchProjectAndOrders();
        });
      }
    } catch (error) {
      catchAndThrowErrors(
        error,
        showError,
        getTranslatedUserMsg(t, "ERROR_MEASUREMENT_DATA"),
      );
    }
  };

  const saveOrder = async (submitOrder?: boolean) => {
    if (checkScanRejection()) {
      return;
    }

    try {
      await saveProject();

      // Order ID is created on initial POST, we'll use its status to differentiate between create and update actions
      if (state?.order?.id) {
        // requested by and project are fields used only by the client, it should not be included in the order payload
        delete state.order?.requestedBy;
        delete state.order?.project;

        const newOrder: ServerOrder = {
          ...state.order,
          poNumber: state.project.poNumber,
          type: state.project.type,
          product: { ...state.product } as ServerProduct,
          features: state.features,
          anchors: state.anchors,
          shipping: state.shipping,
          lathamSpec: state.spec,
          status: OrderStatus.Saved,
          accessories: state.accessories,
          priceOption: state.order?.priceOption ?? state.project.priceOption,
          // Include updatedAt to reflect last time order was saved
          updatedAt: new Date().toISOString(),
        };

        await OrdersService.set(newOrder, "PATCH", false)
          .then(() => {
            dispatch({
              type: "updateOrder",
              payload: { status: OrderStatus.Saved },
            });
            showSuccess(
              state.order?.quoteId
                ? USER_MSG.SUCCESS_ORDER_SAVE
                : USER_MSG.SUCCESS_QUOTE_SAVE,
            );
          })
          .catch((error) => {
            catchAndThrowErrors(
              error,
              showError,
              getTranslatedUserMsg(t, "ERROR_SAVING_ORDER"),
            );
          });
      } else {
        createOrder(submitOrder);
        showSuccess(getTranslatedUserMsg(t, "SUCCESS_QUOTE_CREATE"));
      }
    } catch (err) {
      const error = err as Error;
      catchAndThrowErrors(error, showError, USER_MSG.ERROR_SAVING_ORDER);
    }
    setIsOrderModalOpen(false);
    dispatch({ type: "resetOrder" });
    await fetchOrders();
  };

  const deleteOrder = (order?: ServerOrder) => {
    try {
      const isQuoteMessage =
        !order ||
        order?.status === OrderStatus.QuoteInReview ||
        order?.status === OrderStatus.ReadyForOrder;
      const orderId = state.order?.id ?? order?.id;
      if (project.id && orderId) {
        ProjectService.deleteOrder(project.id, orderId)
          .then(async (response) => {
            setIsOrderModalOpen(false);
            showSuccess(
              isQuoteMessage
                ? USER_MSG.SUCCESS_QUOTE_CANCEL
                : USER_MSG.SUCCESS_ORDER_DELETE,
            );
            // Backend will indicate if DXF regeneration is needed
            // do this before dispatch or fetching orders and project to avoid odd rendering
            if (response?.shouldRegenerateDxf) {
              try {
                await generateDxf();
              } catch (dxfError) {
                console.error(
                  "Failed to regenerate DXF after deleting last Safety Cover order:",
                  dxfError,
                );
              }
            }

            dispatch({ type: "resetOrder" });
            await fetchProjectAndOrders();
          })
          .catch((error) => {
            catchAndThrowErrors(
              error,
              showError,
              isQuoteMessage
                ? USER_MSG.ERROR_DELETING_QUOTE
                : USER_MSG.ERROR_DELETING_ORDER,
            );
          });
      }
    } catch (err) {
      const error = err as Error;
      catchAndThrowErrors(
        error,
        showError,
        getTranslatedUserMsg(t, "ERROR_DELETING_ORDER"),
      );
    }
  };

  const confirmDeleteOrder = (e: React.MouseEvent<HTMLElement>, order: ServerOrder) => {
    const isAfterQuote = order.status === OrderStatus.Ordered;
    setOrderToDelete(order);
    setConfirmHeaderText(
      isAfterQuote
        ? t("orderMsg.deleteOrder", ORDER_MSG.DELETE_ORDER)
        : t("orderMsg.cancelQuote", ORDER_MSG.CANCEL_QUOTE),
    );
    setConfirmBodyText(
      isAfterQuote
        ? t("orderMsg.delete", ORDER_MSG.DELETE)
        : t("orderMsg.cancelQuote", ORDER_MSG.CANCEL_QUOTE),
    );
    e.stopPropagation();
    setIsDeleteConfirmModalOpen(true);
  };

  const archiveOrder = async (order?: ServerOrder) => {
    if (order?.id) {
      // requested by is a field used only by the client, it should not be included in the order payload
      delete order?.requestedBy;

      const newOrder: ServerOrder = {
        ...order,
        archived: !order.archived,
      };

      await OrdersService.set(newOrder, "PATCH", false)
        .then(() => {
          if (newOrder.archived) {
            showSuccess(
              newOrder.status === OrderStatus.Ordered
                ? USER_MSG.SUCCESS_ORDER_ARCHIVE
                : USER_MSG.SUCCESS_QUOTE_ARCHIVE,
            );
          } else {
            showSuccess(
              newOrder.status === OrderStatus.Ordered
                ? USER_MSG.SUCCESS_ORDER_UNARCHIVE
                : USER_MSG.SUCCESS_QUOTE_UNARCHIVE,
            );
          }
        })
        .catch((error) => {
          console.log("failed updating order", error, newOrder);
        });

      await fetchOrders();
    }
  };

  const confirmArchiveOrder = (
    e: React.MouseEvent<HTMLElement>,
    order: ServerOrder,
  ) => {
    const isAfterQuote = order.status === OrderStatus.Ordered;
    setOrderToDelete(order);

    if (isAfterQuote) {
      setConfirmHeaderText(
        order?.archived
          ? t("orderMsg.orderUnarchive", ORDER_MSG.ORDER_UNARCHIVE)
          : t("orderMsg.orderArchive", ORDER_MSG.ORDER_ARCHIVE),
      );
    } else {
      setConfirmHeaderText(
        order?.archived
          ? t("orderMsg.quoteUnarchive", ORDER_MSG.QUOTE_UNARCHIVE)
          : t("orderMsg.quoteArchive", ORDER_MSG.QUOTE_ARCHIVE),
      );
    }
    setConfirmBodyText(
      order?.archived
        ? t("orderMsg.unarchive", ORDER_MSG.UNARCHIVE)
        : t("orderMsg.archive", ORDER_MSG.ARCHIVE),
    );
    e.stopPropagation();
    setIsArchiveConfirmModalOpen(true);
  };

  const cancelQuote = () => {
    try {
      if (project.id && state.order?.id) {
        ProjectService.cancelQuote(project.id, state.order.id)
          .then(() => {
            setIsOrderModalOpen(false);
            showSuccess(getTranslatedUserMsg(t, "SUCCESS_QUOTE_CANCEL"));
            dispatch({ type: "resetOrder" });
            fetchOrders();
          })
          .catch((error) => {
            catchAndThrowErrors(
              error,
              showError,
              getTranslatedUserMsg(t, "ERROR_DELETING_QUOTE"),
            );
          });
      }
    } catch (err) {
      const error = err as Error;
      catchAndThrowErrors(error, showError, USER_MSG.ERROR_DELETING_QUOTE);
    }
  };

  const quoteNeedsReview = () => {
    const features = state.features?.features ?? [];

    const isLinerQuote = state.project?.type === OrderType.Liner;

    if (isLinerQuote) {
      const hasVinylFeature = features.some((feat) => isVinylFeature(feat.featureType));

      // liner quote without VinylFeature → skip review
      if (!hasVinylFeature) {
        return false;
      }
      // liner quote with VinylFeature → review
      return true;
    }

    const isCoverQuote = state.project?.type === OrderType.Cover;
    const hasNonLinerFeatures = features.some(
      (feat) => !isLinerFeature(feat.featureType),
    );
    const hasStepFeatureOnly =
      features.filter((feat) => feat.featureType === FeatureType.Steps).length ===
      state.features?.features?.length;
    const hardware: string[] = [];
    if (state.anchors && state.anchors.decks) {
      state.anchors.decks?.forEach((deck) => {
        const deckHardware = deck.hardware as string;
        !hardware.includes(deckHardware) && hardware.push(deckHardware);
      });
    }

    if (isCoverQuote) {
      // This does not include the default deck feature
      // Basically if the deck table in the ProjectTable component has decks populated, then it needs reviewing
      const hasDeckFeatures = features.some(
        (feat) => feat.featureType === FeatureType.Decking,
      );
      if (hasDeckFeatures) {
        return true;
      }
      // if there are no features and has only 1 anchor type
      if (!hasNonLinerFeatures && hardware.length <= 1) {
        return false;
      }
      // if the only feature type is outside step and has only 1 anchor type
      if (hasStepFeatureOnly && hardware.length <= 1) {
        return false;
      }
      // Filter for cover features only
      const coverFeaturesOnly = features.filter((feat) =>
        checkFeatureOrderType(feat.featureType, OrderType.Cover),
      );
      // If the only cover feature is Anchor, no review needed
      const hasOnlyAnchorFeature =
        coverFeaturesOnly.length === 1 &&
        coverFeaturesOnly[0].featureType === FeatureType.Anchor;

      if (hasOnlyAnchorFeature) {
        return false;
      }

      return true;
    }

    return true;
  };

  const submitToLatham = async () => {
    if (checkScanRejection()) {
      return;
    }

    await saveOrder(true);
    const needsReview = quoteNeedsReview();
    if (!isProjectValid) {
      showError(USER_MSG.ERROR_INCOMPLETE_PROJECT);
    } else if (state.order?.id && !waitingForLathamResponse) {
      let request: Function;
      let successMessage: { title: string; msg: string };
      if (state.order.status === OrderStatus.ReadyForOrder) {
        request = ProjectService.submitQuotedOrder;
        successMessage = getTranslatedUserMsg(t, "SUCCESS_ORDER_SUBMIT");
      } else if (state.order.status === OrderStatus.Saved && needsReview) {
        request = ProjectService.submitQuoteForReview;
        successMessage = getTranslatedUserMsg(t, "SUCCESS_QUOTE_SUBMIT");
      } else {
        request = ProjectService.submitOrderForQuote;
        successMessage = getTranslatedUserMsg(t, "SUCCESS_QUOTE_SUBMIT");
      }

      setWaitingForLathamResponse(true);
      await request(project.id, state.order.id)
        .then(async () => {
          showSuccess(successMessage);
          await fetchProjectAndOrders();
        })
        .catch((error: Error) => {
          catchAndThrowErrors(
            error,
            showError,
            getTranslatedUserMsg(t, "ERROR_MEASUREMENT_DATA"),
          );
        })
        .finally(() => {
          setWaitingForLathamResponse(false);
          setIsOrderModalOpen(false);
          setIsConfirmModalOpen(false);
        });
    }
  };

  useEffect(() => {
    dispatch({ type: "updateStep", payload: currentStep });
    setIsLastStep(currentStep === LAST_STEP);
  }, [currentStep]);

  useEffect(() => {
    if (state.order?.status === OrderStatus.Ordered) {
      setCurrentStep(LAST_STEP);
    }
  }, [state.order?.status]);

  useEffect(() => {
    if (!submitUrl) return;
    if (isProjectValid) {
      openEmailClient();
    } else {
      showError(USER_MSG.ERROR_INCOMPLETE_PROJECT);
    }
  }, [submitUrl]);

  useEffect(() => {
    if (project.id) {
      fetchOrders();
      fetchFeatures();
    } else {
      console.warn("fetching no has id");
      console.warn(project);
    }
  }, [project.id]);

  useEffect(() => {
    if (project.id && state.project?.type) {
      fetchFeatures();
    } else {
      console.warn("fetching no has id");
      console.warn(project);
    }
  }, [state.project?.type]);

  useEffect(() => {
    const text = waitingForLathamResponse
      ? t("component.processing", "Processing...")
      : state.order?.status === OrderStatus.Saved
        ? t("component.orders.submitQuote", "Submit for Quote")
        : t("component.orders.submitOrderToLatham", "Submit Order to Latham");

    setConfirmButtonText(text);
  }, [state.order?.status, waitingForLathamResponse]);

  useEffect(() => {
    const title = getTitle();
    setModalTitle(title);
  }, [state.order?.status]);

  const getTitle = (): OrderModalTitle => {
    if (state.order?.id) {
      switch (state.order.status) {
        case OrderStatus.Ordered:
          return t("component.orders.reviewOrder", "Review Order") as OrderModalTitle;
        case OrderStatus.QuotedOptions:
        case OrderStatus.ReadyForOrder: {
          const productType =
            state.order.type === OrderType.Liner
              ? t("component.orders.liner", "Liner")
              : t("component.orders.cover", "Cover");
          return t(
            "component.orders.finalizeOrder",
            "Finalize Order - {{productType}}",
            { productType },
          ) as OrderModalTitle;
        }
        default:
          return t("component.orders.resumeQuote", "Resume Quote") as OrderModalTitle;
      }
    }
    return t("component.orders.newQuote", "New Quote") as OrderModalTitle;
  };

  const getLatestQuote = (order: ServerOrder): OrderQuote | undefined => {
    const latestOrder = order.quotes?.find(
      (quote) => quote.id === order.quoteId && quote.status === OrderQuoteStatus.Valid,
    );

    return latestOrder;
  };

  const getQuoteDateText = (order: ServerOrder): string => {
    let subject: string;
    let dateObject: string;
    switch (order?.status) {
      case OrderStatus.Saved: {
        subject = t("component.orders.lastSaved", "Last Saved");
        dateObject = order.updatedAt;
        break;
      }
      case OrderStatus.QuotedOptions:
      case OrderStatus.ReadyForOrder: {
        subject = t("component.orders.submittedQuote", "Submitted Quote");
        dateObject = (getLatestQuote(order)?.quotedAt as string) ?? order.updatedAt;
        break;
      }
      case OrderStatus.Ordered: {
        subject = t("component.orders.ordered", "Ordered");
        dateObject = order.updatedAt;
        break;
      }
      case OrderStatus.QuoteInReview: {
        subject = t("component.orders.quoteInReview", "Quote In Review");
        dateObject = order.updatedAt;
        break;
      }
      default:
        return "";
    }

    return getDateAndTime(subject, dateObject);
  };

  const toggleArchivedFilter = () => {
    setShowArchived(!showArchived);
  };

  const getArchiveText = () => {
    return showArchived
      ? t("component.orders.hideArchived", "Hide archived Quotes and Orders")
      : t("component.orders.showArchived", "Show archived Quotes and Orders");
  };

  const disableQuoteAndOrderForVinylLiner =
    (state.project?.poolShape?.baseName ?? state.project?.poolShapeType) ===
      t("component.orders.fiberglassGeometric", "Fiberglass Geometric") ||
    (state.project?.poolShape?.baseName ?? state.project?.poolShapeType) ===
      t("component.orders.fiberglassFreeform", "Fiberglass Freeform");

  const coverQuotesCard = orders?.filter((o) => {
    if (o.status !== OrderStatus.Ordered && o.type !== OrderType.Liner) {
      if (!showArchived) {
        return !o.archived;
      }
      return o;
    }
  });

  const linerQuotesCard = orders?.filter((o) => {
    if (o.status !== OrderStatus.Ordered && o.type === OrderType.Liner) {
      if (!showArchived) {
        return !o.archived;
      }
      return o;
    }
  });

  const coverOrderCard = orders?.filter((o) => {
    if (o.status === OrderStatus.Ordered && o.type !== OrderType.Liner) {
      if (!showArchived) {
        return !o.archived;
      }
      return o;
    }
  });

  const linerOrderCard = orders?.filter((o) => {
    if (o.status === OrderStatus.Ordered && o.type === OrderType.Liner) {
      if (!showArchived) {
        return !o.archived;
      }
      return o;
    }
  });

  const isSafetyCoverQuotesEmpty = coverQuotesCard.length === 0;
  const isLinerQuotesEmpty = linerQuotesCard.length === 0;
  const isSafetyCoverOrderEmpty = coverOrderCard.length === 0;
  const isLinerOrderEmpty = linerOrderCard.length === 0;

  const panes = [
    {
      menuItem: t("component.orders.quotes", "Quotes"),
      render: () => (
        <Tab.Pane attached={false}>
          {
            <div className={"order-card-group"}>
              {hasAccess && (
                <Card
                  onClick={() => {
                    openModal(state.order);
                  }}
                  className={"create-quote-button"}
                  data-cy={"create-order-button"}
                >
                  <Cross className={"create-quote-box1"} />
                  <div className={"create-quote-box2"}>
                    {t("component.orders.addNewQuote", "Add New Quote")}
                  </div>
                </Card>
              )}
              {flags.showLinerUi && !disableQuoteAndOrderForVinylLiner ? (
                <div className={"order-feature-card-group"}>
                  <div
                    className={`cover-card-group ${
                      isSafetyCoverQuotesEmpty ? "cover-empty" : ""
                    }`}
                  >
                    <Header size={"medium"} className={"feature-header"}>
                      {t("component.orders.cover", "Cover")}
                    </Header>
                    {coverQuotesCard.map((order, i) => (
                      <OrderCard
                        key={i}
                        hasArchiveAccess={hasArchiveAccess}
                        hasDeleteAccess={hasDeleteAccess}
                        isDistributorDealer={isDistributorDealer}
                        order={order}
                        openModal={openModal}
                        confirmArchiveOrder={confirmArchiveOrder}
                        confirmDeleteOrder={confirmDeleteOrder}
                        getQuoteDateText={getQuoteDateText}
                        getLatestQuote={getLatestQuote}
                      />
                    ))}
                  </div>
                  <div className={"vertical-line"} />
                  <div
                    className={`liner-card-group ${
                      isLinerQuotesEmpty ? "liner-empty" : ""
                    }`}
                  >
                    <Header size={"medium"} className={"feature-header"}>
                      {t("component.orders.liner", "Liner")}
                    </Header>
                    {linerQuotesCard.map((order, i) => (
                      <OrderCard
                        key={i}
                        hasArchiveAccess={hasArchiveAccess}
                        hasDeleteAccess={hasDeleteAccess}
                        isDistributorDealer={isDistributorDealer}
                        order={order}
                        openModal={openModal}
                        confirmArchiveOrder={confirmArchiveOrder}
                        confirmDeleteOrder={confirmDeleteOrder}
                        getQuoteDateText={getQuoteDateText}
                        getLatestQuote={getLatestQuote}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={"card-group"}>
                  {coverQuotesCard.map((order, i) => (
                    <OrderCard
                      key={i}
                      hasArchiveAccess={hasArchiveAccess}
                      hasDeleteAccess={hasDeleteAccess}
                      isDistributorDealer={isDistributorDealer}
                      order={order}
                      openModal={openModal}
                      confirmArchiveOrder={confirmArchiveOrder}
                      confirmDeleteOrder={confirmDeleteOrder}
                      getQuoteDateText={getQuoteDateText}
                      getLatestQuote={getLatestQuote}
                    />
                  ))}
                </div>
              )}
            </div>
          }
        </Tab.Pane>
      ),
    },
    {
      menuItem: t("component.orders.orders", "Orders"),
      render: () => (
        <Tab.Pane attached={false}>
          {
            <div className={"order-card-group"}>
              {flags.showLinerUi ? (
                <div className={"order-feature-card-group"}>
                  <div
                    className={`cover-card-group ${
                      isSafetyCoverOrderEmpty ? "cover-empty" : ""
                    }`}
                  >
                    <Header size={"medium"} className={"feature-header"}>
                      {t("component.orders.cover", "Cover")}
                    </Header>
                    {coverOrderCard.map((order, i) => (
                      <OrderCard
                        key={i}
                        hasArchiveAccess={hasArchiveAccess}
                        hasDeleteAccess={hasDeleteAccess}
                        isDistributorDealer={isDistributorDealer}
                        order={order}
                        openModal={openModal}
                        confirmArchiveOrder={confirmArchiveOrder}
                        confirmDeleteOrder={confirmDeleteOrder}
                        getQuoteDateText={getQuoteDateText}
                        getLatestQuote={getLatestQuote}
                      />
                    ))}
                  </div>
                  <div className={"vertical-line"} />
                  <div
                    className={`liner-card-group ${
                      isLinerOrderEmpty ? "liner-empty" : ""
                    }`}
                  >
                    <Header size={"medium"} className={"feature-header"}>
                      {t("component.orders.liner", "Liner")}
                    </Header>
                    {linerOrderCard.map((order, i) => (
                      <OrderCard
                        key={i}
                        hasArchiveAccess={hasArchiveAccess}
                        hasDeleteAccess={hasDeleteAccess}
                        isDistributorDealer={isDistributorDealer}
                        order={order}
                        openModal={openModal}
                        confirmArchiveOrder={confirmArchiveOrder}
                        confirmDeleteOrder={confirmDeleteOrder}
                        getQuoteDateText={getQuoteDateText}
                        getLatestQuote={getLatestQuote}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={"card-group"}>
                  {coverOrderCard.map((order, i) => (
                    <OrderCard
                      key={i}
                      hasArchiveAccess={hasArchiveAccess}
                      hasDeleteAccess={hasDeleteAccess}
                      isDistributorDealer={isDistributorDealer}
                      order={order}
                      openModal={openModal}
                      confirmArchiveOrder={confirmArchiveOrder}
                      confirmDeleteOrder={confirmDeleteOrder}
                      getQuoteDateText={getQuoteDateText}
                      getLatestQuote={getLatestQuote}
                    />
                  ))}
                </div>
              )}
            </div>
          }
        </Tab.Pane>
      ),
    },
  ];

  return (
    <TransitionGroup isVisible={!!orders}>
      <Segment className={"pad"}>
        <Header size={"large"}>
          {t("component.orders.quotesAndOrders", "Quotes and Orders")}
        </Header>
        <button
          className={"archive-button"}
          onClick={() => {
            toggleArchivedFilter();
          }}
        >
          {getArchiveText()}
        </button>
        <Tab menu={{ pointing: true }} className={"orders-tab"} panes={panes} />
      </Segment>
      <Confirm
        size={"mini"}
        open={isDeleteConfirmModalOpen}
        header={confirmHeaderText}
        content={t("section.orders.areYouSure", "Are you sure?")}
        onConfirm={() => {
          deleteOrder(orderToDelete);
          setIsDeleteConfirmModalOpen(false);
        }}
        confirmButton={confirmBodyText}
        onCancel={() => {
          setIsDeleteConfirmModalOpen(false);
        }}
      />
      <Confirm
        size={"mini"}
        open={isArchiveConfirmModalOpen}
        header={confirmHeaderText}
        content={t("section.orders.areYouSure", "Are you sure?")}
        onConfirm={() => {
          archiveOrder(orderToDelete);
          setIsArchiveConfirmModalOpen(false);
        }}
        confirmButton={confirmBodyText}
        onCancel={() => {
          setIsArchiveConfirmModalOpen(false);
        }}
      />
      <OrderModal
        title={modalTitle}
        cancelOrder={cancelOrder}
        submitOrder={openConfirmationModal}
        currentOrder={state.order}
        currentStep={currentStep}
        previousStep={() => {
          setCurrentStep(currentStep - 1);
        }}
        nextStep={() => {
          goToStep(currentStep + 1);
        }}
        isLastStep={isLastStep}
        finalStep={LAST_STEP}
        initialStep={INITIAL_STEP}
        goToStep={goToStep}
        isOpen={isOrderModalOpen}
        hasAccess={hasReviewAccess}
        rejectScan={rejectScan}
        restoreScan={restoreScan}
      >
        <>
          {state.order?.status !== OrderStatus.Ordered && !state.order?.archived && (
            <SaveButton onSave={saveOrder} />
          )}
        </>
        <>
          {state.order?.status === OrderStatus.ReadyForOrder && hasRevertAccess && (
            <RevertQuoteButton onClick={() => revertQuote()} />
          )}
        </>
        <Stepper
          currentStep={currentStep}
          project={project}
          goToStep={goToStep}
          deleteOrder={deleteOrder}
          cancelQuote={cancelQuote}
          hiddenLoading={!isOrderModalOpen}
        />
      </OrderModal>
      <Confirm
        size={"mini"}
        open={isConfirmModalOpen}
        header={confirmButtonText}
        content={
          <Form>
            <Form.Field>
              <Ref innerRef={emailMsg}>
                <textarea
                  id={"msg"}
                  cols={30}
                  rows={10}
                  placeholder={t(
                    "component.orders.enterMessage",
                    t("component.orders.enterMessage", "You can enter a message here"),
                  )}
                />
              </Ref>
            </Form.Field>
          </Form>
        }
        onConfirm={submitToLatham}
        confirmButton={confirmButtonText}
        onCancel={() => {
          setIsConfirmModalOpen(false);
        }}
      />
      <Modal open={waitingForLathamResponse}>
        <div className={"ui large text loader"}>
          {t("component.processing", "Processing...")}
        </div>
      </Modal>
    </TransitionGroup>
  );
}
